import { ReactNode } from "react";
import { Box, Stack, Typography, ListItemButton } from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import moment from "moment";
import { convertESTtoUserLocalDateAndTime } from "helpers/methods";

interface SearchResultRowProps {
  isFolder: boolean;
  title: string;
  crumbs: string[];
  createdBy?: string;
  createdOn?: string;
  onClick: () => void;
  /**
   * Optional trailing controls (e.g. a "more options" menu) rendered as a
   * sibling of the row button, not inside it — so the control keeps its own
   * click target without nesting a button in a button.
   */
  actions?: ReactNode;
  /**
   * Optional inline marker rendered beside the title (e.g. a status chip). Wraps
   * under the title on narrow rows rather than squeezing it.
   */
  badge?: ReactNode;
}

const wrapSx = {
  wordBreak: "break-word" as const,
  overflowWrap: "anywhere" as const,
  whiteSpace: "normal" as const,
};

// List data comes back as EST "MM/DD/YYYY HH:mm:ss"; search results may return
// an ISO timestamp. Try the strict EST format first, then fall back to a
// generic parse so the date shows in both cases.
const formatCreatedOn = (raw?: string): string => {
  if (!raw) return "";
  const est = convertESTtoUserLocalDateAndTime(
    raw,
    "MMM D, YYYY h:mm A",
    "MM/DD/YYYY HH:mm:ss",
  );
  if (est && est !== "Invalid date") return est;
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : "";
};

const SearchResultRow = ({
  isFolder,
  title,
  crumbs,
  createdBy,
  createdOn,
  onClick,
  actions,
  badge,
}: SearchResultRowProps) => {
  const pathLabel = crumbs.length ? crumbs.join(" / ") : "Home";
  const createdOnLabel = formatCreatedOn(createdOn);
  const hasMeta = Boolean(createdBy || createdOnLabel);

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", width: "100%" }}>
      <ListItemButton
        onClick={onClick}
        alignItems="flex-start"
        sx={{ py: 1, px: 1.5, flex: 1, minWidth: 0 }}
      >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        width="100%"
        minWidth={0}
      >
        {isFolder ? (
          <FolderOutlinedIcon
            fontSize="small"
            sx={{ mt: 0.3, color: "#6b7280", flexShrink: 0 }}
          />
        ) : (
          <DescriptionOutlinedIcon
            fontSize="small"
            sx={{ mt: 0.3, color: "#6b7280", flexShrink: 0 }}
          />
        )}

        <Box minWidth={0} flex={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              title={title}
              sx={wrapSx}
            >
              {title || "(untitled)"}
            </Typography>
            {badge}
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="flex-start"
            sx={{ mt: 0.5 }}
          >
            <FolderOutlinedIcon
              sx={{
                fontSize: 14,
                color: "text.secondary",
                mt: "2px",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              title={pathLabel}
              sx={wrapSx}
            >
              {pathLabel}
            </Typography>
          </Stack>
        </Box>

        {hasMeta && (
          <Box
            sx={{
              flexShrink: 0,
              textAlign: "right",
              pl: 1,
              maxWidth: 170,
            }}
          >
            {createdBy && (
              <Typography variant="caption" color="text.secondary" sx={wrapSx}>
                Created by {createdBy}
              </Typography>
            )}
            {createdOnLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ...wrapSx, display: "block" }}
              >
                {createdOnLabel}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
      </ListItemButton>
      {actions && (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{ display: "flex", alignItems: "center", pr: 0.5, flexShrink: 0 }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default SearchResultRow;
